import { useState } from 'react';
import { useTaskDetail, type TaskDetail } from '../../../lib/use-task-detail';
import { useAgentName, type AgentName } from '../../../lib/use-agent-name';
import { useMyAgents } from '../../../lib/my-agents';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { TASK_DETAILS_PAGE } from '../../../lib/tasks-bi';
import { taskStatusBadge } from '../../../lib/task-status';
import { gatingBlocker, isScheduledTask, scheduledDateLabel } from '../../../lib/task-schedule';
import { awaitsDecision, bucketOfTask, isInMotion, isNotStarted } from '../../../lib/agent-task-buckets';
import { taskChatOrigin, taskGoalOrigin, type GoalOrigin } from '../../../lib/task-origin';
import { useWorkspaces } from '../../../lib/workspaces';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { useTaskFiles, type TaskFile } from '../../../lib/task-files';
import { docViewerKind } from '../../../lib/doc-viewer';
import { openDocViewer } from '../../../lib/doc-viewer-presenter';
import { useCancelTask, type CancelTaskFlow } from '../../../lib/use-cancel-task';
import { useSentOutreaches, type SentOutreachSummary } from '../../../lib/use-sent-outreaches';
import { taskKnownAction } from '../../../lib/task-known-action';
import { taskDiscussMessage, taskResolveMessage } from '../../../lib/task-resolve-message';
import { taskActions, type TaskAction } from '../../../lib/task-actions';
import { taskAcceptsUserEdits } from '../../../lib/task-editable';
import { useTaskEdits, taskNotes, type TaskEditsFlow } from '../../../lib/use-task-edits';
import { useTaskViewedSignal } from '../../../lib/use-task-viewed-signal';

type StatusVariant = ReturnType<typeof taskStatusBadge>['variant'];

/** One offered action, localized and bound — renderable as a button or as a menu row unchanged. */
export interface TaskMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

export type { TaskFile };

interface Base {
  agent: AgentName;
  close: () => void;
  /** Return to the drill-in this task was opened FROM, when there is one. Undefined for a task opened
   *  straight off the Tasks list, which came from no drill-in and must grow no back affordance —
   *  the decision is the modal stack's depth, not anything this screen knows. */
  back?: { label: string; onBack: () => void };
}

export interface TaskDetailReadyVm extends Base {
  kind: 'ready';
  task: TaskDetail;
  title: string;
  description: string | undefined;
  agentName: string;
  /** The step in motion: the FIRST incomplete one (-1 when all are done). */
  currentStep: number;
  statusVariant: StatusVariant;
  /** This task was laid out and never started — PENDING, undated, no agent ever put on it. The drill-in
   *  reads quietly for it (the neutral "Not started" chip) instead of claiming work is under way, the
   *  same derived treatment the list row gets. */
  notStarted: boolean;
  /** An agent is executing on this row right now — the badge reads "Working" instead of "Active Task". */
  inMotion: boolean;
  /** A dated task held for a future instant (`isScheduledTask`): the chip text ("Thu" / "9 Oct"); empty otherwise. */
  scheduledLabel: string;
  /** The full local date a scheduled task starts, for the "Scheduled for …" line; empty otherwise. */
  scheduledWhen: string;
  /** When the task was created, as a local date + time for the "Created {date}" line; empty when the
   *  task carries no readable date. Same medium date + short time both phones render. */
  created: string;
  /** The task this one waits for (`gatingBlocker`), when it is gated; `when` is that blocker's own date
   *  when it has one. What lets the detail say "Starts after Check-in round 4 · Thu, 9 Oct". */
  gating: { id: string; title: string; when: string } | null;
  /** The goal that laid this task out, named and linked — the detail's half of the provenance the row
   *  carries. Null when the task serves no goal, or its goal could not be read. */
  goalOrigin: GoalOrigin | null;
  /** The chat the task was born in, when there is no goal to name instead — see `taskChatOrigin`. */
  chatOrigin: { conversationId: string } | null;
  /** Opens that chat in place (closing the drill-in first), rather than sending anything to it. */
  onOpenChatOrigin: () => void;
  /** Held on an open decision, whatever the status says — same derivation and same badge precedence as
   *  the list row (`awaitsDecision`, ahead of `notStarted`). Goes false the instant an answer lands here,
   *  via `onDecisionAnswered`, so the badge stops claiming the reader is needed before the re-read lands. */
  awaitingDecision: boolean;
  /** Called by the decision block with whether every question on the task is now answered. */
  onDecisionAnswered: (allAnswered: boolean, decisionId: string) => void;
  answeredDecisionIds: string[];
  /** The primary CTA: its label, and what pressing it does. Which action that is comes from
   *  `lib/task-actions.ts` — the task's bucket picks the Maestro wording, and a task whose text names a
   *  known next step promotes that step instead. Built here so both layouts render one decision. */
  cta: TaskMenuItem;
  /** Everything else on offer, for the 3-dot menu: the displaced Maestro handoff when a known action took
   *  the button, and Cancel Task while the task is still open. Cancel is menu-only by design. */
  menu: TaskMenuItem[];
  onResolve: () => void;
  /** The files the task produced, listed off the file-system by origin (see `lib/task-files.ts`).
   *  Empty when the task produced none — the Files section then renders nothing. */
  files: TaskFile[];
  /** Open one produced file in the in-app doc viewer. The content came WITH the list, so this is a
   *  pure presentation call — no resolve, and no chip that can turn out to be dead. */
  onOpenFile: (file: TaskFile) => void;
  /** The confirm-then-cancel flow, shared with the list row's overflow menu. */
  cancel: CancelTaskFlow;
  /** Whether the person may still RESTATE this task or NOTE on it. The same rule as "may they cancel it"
   *  (`lib/task-editable.ts`) rather than a second status table — a screen offering a note where it
   *  hides a cancel is exactly the drift one shared rule prevents. */
  editable: boolean;
  /** The two user-authored writes: restate, and note. */
  edits: TaskEditsFlow;
  /** The notes the user has written on this task, oldest first. Shown whatever the task's state — they
   *  are history by the time it settles, and history does not stop being true. */
  notes: TaskDetail['notes'];
  /** What the task's outreaches came to — who they went to, WHAT WAS SAID, and whether anyone wrote back.
   *  Undefined only when the task sent none. The already-loaded task list is preferred (free), and the
   *  detail's own read backs it up so the section is present however the detail was reached — a task
   *  opened from a goal's plan has no loaded list at all, and used to show nothing.
   *  See `lib/use-sent-outreaches.ts` and the route's `sentOutreachesForTask`. */
  sentOutreaches?: SentOutreachSummary[];
}

export type TaskDetailModalVm =
  | (Base & { kind: 'loading' })
  | (Base & { kind: 'error' })
  | TaskDetailReadyVm;

// Everything the task-details drill-in DOES, independent of how it looks — the read, the agent/step
// derivation, the BI, and the close / "Resolve with {agent}" actions. The desktop centered modal and
// the mobile full-screen sheet are two separate UIs over this one view model (Figma 12801-105529 wide,
// 12175-102348 narrow), so the behaviour, copy and analytics stay identical across the two surfaces.
export function useTaskDetailModal(taskId: string): TaskDetailModalVm {
  const state = useTaskDetail(taskId);
  // Ledger TASK_VIEWED (smart notifications §5.7): the person is looking at this task — once per task
  // shown, on both layouts, the same way both phones report it from their task detail.
  useTaskViewedSignal(taskId);
  // Read before the loading/error early-returns: a hook cannot be called conditionally, and the value is
  // a cache lookup rather than a fetch, so there is nothing to defer.
  const sentOutreaches = useSentOutreaches(taskId);
  const { t, i18n } = useTranslation();
  const agent = useAgentName();
  const openSideChat = useOpenSideChat();
  // Opening the ORIGIN chat is not a handoff: nothing is sent, the thread is simply shown. `openSideChat`
  // refuses an empty message by design, so the origin line uses the same two calls `openMaestroChat` does.
  const { setActiveConversationId, setChatMode } = useActiveConversation();
  const { closeModal, back: popModal, backLabel } = useTaskSidePanel();
  const { logPageClickBi, logModalActionBi } = useGenericBi('task-details');
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  // Only the roster knows a Space's slug, and only the slug makes a goal's route resolvable — same lookup
  // the Tasks list does for the same line (`lib/task-origin.ts`).
  const workspacesState = useWorkspaces();
  const files = useTaskFiles(taskId);
  const cancel = useCancelTask(TASK_DETAILS_PAGE);
  const edits = useTaskEdits(taskId);
  // The one thing the drill-in cannot know until the re-read lands: an answer just landed a moment ago.
  // Same shape as the list row's `answeredHere` — the badge stops claiming the reader is needed the
  // instant the last question is answered, not on the next open.
  const [answeredHere, setAnsweredHere] = useState(false);
  const [answeredDecisionIds, setAnsweredDecisionIds] = useState<string[]>([]);
  useLoadBi('task-detail-modal', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const close = () => {
    logModalActionBi({ ...TASK_DETAILS_PAGE, modal_name: 'task_detail', action: 'close' });
    closeModal();
  };

  // Only when the stack HAS something underneath, and only with a label to name it — a back control
  // that cannot say where it goes is indistinguishable from a second close.
  const back =
    popModal && backLabel
      ? {
          label: backLabel,
          onBack: () => {
            logModalActionBi({ ...TASK_DETAILS_PAGE, modal_name: 'task_detail', action: 'back' });
            popModal();
          },
        }
      : undefined;

  if (state.kind !== 'ready') {
    return { kind: state.kind, agent, close, back };
  }

  const { task } = state;
  const agentName = task.agentId ? team.find((a) => a.id === task.agentId)?.name?.trim() || '' : '';
  const description = task.summary?.trim() || task.currentAction?.trim();
  const title = task.title?.trim() || t('tasks.untitledTask');
  // The step IN MOTION — the first incomplete one. An ENDED task has none: a task that completed, was
  // cancelled, or was retired is over, so no step of it is being worked on and nothing on the timeline
  // may claim otherwise. Reported alongside the green-"Done"-on-a-cancelled-task badge: after cancelling,
  // a step still carried the in-progress marker. `failed` is deliberately NOT ended here — the design
  // marks the step a task died on with its own ✕, which is information, not a claim of activity.
  const ended = bucketOfTask(task) === 'done';
  const currentStep = ended ? -1 : task.steps.findIndex((step) => !step.completed);
  const notStarted = isNotStarted(task);
  const inMotion = isInMotion(task);
  // The other two quiet states beside not-started, by the shared rule — the detail used to badge a task
  // held for Sep 28 "In progress", the exact drift the list row had already been cured of.
  const now = Date.now();
  const locale = i18n.language || undefined;
  const scheduled = isScheduledTask(task, now);
  const scheduledLabel = scheduled ? scheduledDateLabel(task.notBefore, now, locale) : '';
  const scheduledWhen = scheduled ? fullDateLabel(task.notBefore, locale) : '';
  const blocker = scheduled ? undefined : gatingBlocker(task, now);
  const gating = blocker ? { id: blocker.id, title: blocker.title, when: fullDateLabel(blocker.notBefore, locale) } : null;
  // Held on an open question, whatever the status says — the list row's own rule (`awaitsDecision`), so
  // the drill-in cannot disagree with the row a reader just tapped to get here.
  const awaitingDecision = awaitsDecision(task) && !answeredHere;
  const statusVariant = taskStatusBadge(task.status, { notStarted, awaitingDecision }).variant;
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const goalOrigin = taskGoalOrigin(task, workspaces.find((w) => w.id === task.workspaceId)?.slug);
  // The chat the task was born in — the fallback origin, shown only when there is no goal to name. A task
  // laid out by a goal came from that goal in the sense the reader cares about; the conversation the tool
  // ran in is plumbing, and two origin lines on one task would say so twice.
  const chatOrigin = goalOrigin ? null : taskChatOrigin(task);
  const onOpenChatOrigin = () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'open_source_chat', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    closeModal();
    if (chatOrigin) setActiveConversationId(chatOrigin.conversationId);
    setChatMode('expanded');
  };

  const onResolve = () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'resolve_with_maestro', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    closeModal();
    // On an ACTION_NEEDED task the message carries the blocker and what "resolve" means — see
    // lib/task-resolve-message.ts (the fabricated-completion incident lives in its header).
    const message = taskResolveMessage(t, { title, status: task.status, summary: task.summary });
    // Continue the Maestro chat the task came from, so it resolves with the history rather than being
    // reintroduced to its own task. Absent when the origin is a routine/goal thread — see the field.
    openSideChat(
      task.resolveConversationId
        ? { kind: 'existing', conversationId: task.resolveConversationId, message, taskId: task.id }
        : { kind: 'new', message, taskId: task.id },
    );
  };

  // "Discuss" / "Follow up" are conversations ABOUT the task rather than a step it is waiting on, so they
  // seed a fresh chat — matching native, where only the resolve action continues the origin thread.
  const onChatAbout = () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'chat_about_task', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    closeModal();
    openSideChat({ kind: 'new', message: taskDiscussMessage(t, title), taskId: task.id });
  };

  // The live view is the ONLY place a paused session can be cleared, so it opens in a new tab rather than
  // replacing the task the user is reading.
  const knownAction = taskKnownAction(task);
  const onGoToBrowseSession = () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'go_to_browsing_session', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    if (knownAction) window.open(knownAction.url, '_blank', 'noopener,noreferrer');
  };

  const bind = (action: TaskAction): TaskMenuItem => ({
    key: action.kind,
    // Only the Maestro labels carry `{agent}`; i18next drops a var the string doesn't use.
    label: t(action.labelKey, { agent: agent.short }),
    destructive: action.destructive,
    onClick:
      action.kind === 'openBrowser'
        ? onGoToBrowseSession
        : action.kind === 'resolve'
          ? onResolve
          : action.kind === 'cancel'
            ? () => cancel.request({ id: task.id, title })
            : onChatAbout,
  });

  const actions = taskActions(task.status, !!knownAction);
  const cta = bind(actions.primary);
  const menu = actions.menu.map(bind);

  // Present the root-level doc viewer on the body the list already carries. An upload's bytes live at
  // `referenceUrl` with an empty `content`; the viewer's "content that is a bare URL" convention is what
  // both shapes collapse into, so it stays one code path.
  const onOpenFile = (file: TaskFile) => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'open_file', task_id: task.id },
      {
        entity: { id: task.id, type: ENTITY_TYPE.task },
        additionalFields: { filename: file.name },
      },
    );
    const content = file.content || file.referenceUrl || '';
    openDocViewer({
      file: { name: file.name, isApp: false, content },
      kind: docViewerKind(file.name, content || undefined),
      pageName: 'document_viewer',
    });
  };

  return {
    kind: 'ready',
    agent,
    close,
    back,
    editable: taskAcceptsUserEdits(task),
    edits,
    notes: taskNotes(task),
    cta,
    menu,
    onResolve,
    notStarted,
    inMotion,
    scheduledLabel,
    scheduledWhen,
    created: createdLabel(task.createdDate, locale),
    gating,
    awaitingDecision,
    onDecisionAnswered: (allAnswered: boolean, decisionId: string) => {
      setAnsweredHere(allAnswered);
      setAnsweredDecisionIds((ids) => (ids.includes(decisionId) ? ids : [...ids, decisionId]));
    },
    answeredDecisionIds,
    goalOrigin,
    chatOrigin,
    onOpenChatOrigin,
    task,
    title,
    description,
    agentName,
    currentStep,
    statusVariant,
    files,
    onOpenFile,
    cancel,
    // The list cache first (already in hand, no request), the detail's own read as the fallback that
    // works from anywhere. An empty array from the route means "this task sent nothing", and normalizing
    // it to undefined keeps ONE meaning of absent for the section to branch on.
    sentOutreaches: sentOutreaches?.length ? sentOutreaches : task.sentOutreaches?.length ? task.sentOutreaches : undefined,
  };
}

function createdLabel(value: string | null, locale: string | undefined): string {
  if (!value) return '';
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** A full local date for the detail's own line ("Thu, 9 Oct"); empty on an absent or unparseable value. */
function fullDateLabel(value: string | Date | undefined, locale: string | undefined): string {
  if (!value) return '';
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}
